/**
@format
@flow local-strict
*/
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { Auth } from 'aws-amplify';
import styled from 'styled-components';
import BannerNotification from '@ziynx/components/notifications';
import { Button } from '@ziynx/components/buttons';
// import Colors from '@ziynx/theme/colors';
// import { Body } from '@ziynx/components/typography';
import Input from '@ziynx/components/inputs';
import Image from '@ziynx/components/images';
import { withAppManager, emailRegex } from '@ziynx/components/utilities';
import { ContentContainer } from '@ziynx/components/layout';
import { Form, FormRow } from '@ziynx/components/forms';
import Link from '@ziynx/components/links';
import ActionTypes from '@ziynx/action-types';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import logo from '../../assets/ZIYNX_LOGO_2.png';
import logoSmall from '../../assets/ZIYNX_SYMBOL_2 2x.png';

type Props = $ReadOnly<{|

  /**
   * Navigation stack
   */
  history: Object,
|}>;

/**
 * Function to get padding for styledBodyWrapper
 */
const getPadding = (smallPadding: boolean, lastChild: boolean): number => {
  if (lastChild) {
    return 0;
  } if (smallPadding) {
    return 40;
  }
  return 48;
};

// display table so IE is not left out
const FormSectionWrapper = styled.div`
  display: table;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '0px 16px 48px' : '0px 40px 48px')};
  ${({ isMobile }) => !isMobile && 'margin: 0 auto;'}
`;

const StyledBannerWrapper = styled.div`
  z-index: 996;
  width: 100%;
  height: 0;
`;

const StyledBodyWrapper = styled.div`
  padding-bottom: ${({ smallPadding, lastChild }) => getPadding(smallPadding, lastChild)}px;
  ${({ center }) => center && 'text-align: center;'}
  ${({ half }) => half
    && `display: flex; 
    flex-direction: row-reverse; 
    justify-content: space-between; 
    align-items: center;`}
`;

const StyledButton = styled(Button)`
  margin-bottom: 24px;
`;

const ImageWrapper = styled.div`
  padding-top: ${({ isMobile }) => (isMobile ? '32px' : '48px')};
  padding-bottom: 64px;
`;

const StyledInput = styled(Input)`
  :-webkit-autofill::first-line {
    font-size: 16px;
    font-family: Nunito;
  }
`;

/**
 * Object to hold data for each types of inputs to
 * be used in Controller
 */
const inputsData = {
  data: [
    {
      id: '1',
      name: 'email',
      defaultValue: '',
      regExPattern: emailRegex,
      errorMessage: 'Please enter a valid email address',
    },
    {
      id: '2',
      name: 'password',
      defaultValue: '',
      regExPattern: undefined,
      errorMessage: undefined,
    },
  ],
};

/**
 * Time for banner to setTimeout
 */
const bannerTimeOut = 5000;

/**
 * Object to store common error messages for banner notification
 */
const bannerMessageData = {
  accountNotFoundError:
    "We couldn't find an account matching the email and password you entered. Please check your email and password again.",
  maxAttemptReConfirmError:
    'Your account has not been verified and you have reached maximum attempt to request a code. Please try again later.',
  generalReConfirmError:
    'Your account has not been verified and we ran into some trouble retrieving your code to confirm. Please try again later.',
  appUserError:
   'Login attempt failed. Please use the the ZIYNX mobile app to view your Talent Profile.',
};

/**
 * Login Screen
 */
const LogInScreen = (props: ManagedProps<Props>): React$Node => {
  const { history, viewport } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  // Currently validates form on submit then re-validate inputs if error
  const {
    handleSubmit, control, errors, formState, clearErrors, getValues,
  } = useForm();

  const isMobileSmall = viewport === 'mobileSmall';
  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  // Fields to watch for changes or errors
  const { dirtyFields } = formState;

  // When there's an error with signing in and others scenarios
  const [hasError, setHasError] = useState(false);
  // state for banner error message based on hasError
  const [bannerErrorMessage, setBannerErrorMessage] = useState('');
  // State to keep track if banner should be rendered
  const [shouldRenderBanner, setRenderBanner] = useState(false);
  // Keep track while waiting for response from AWS
  const [isLoading, setIsLoading] = useState(false);

  const [username, setUsername] = useState(getValues('email'));

  /**
   * Routes user to home page after successful auth
   */
  const login = async (): Promise<any> => {
    dispatch({ type: ActionTypes.auth.checkIfLoggedIn.START });

    if (location?.state?.from) {
      history.replace(location?.state?.from);
    } else {
      history.push('/');
    }
  };

  /**
   * Function to signal there's an error
   * and render banner
   */
  const setBannerError = (): void => {
    setHasError(true);
    setRenderBanner(true);
  };

  useEffect(() => {
    if (location?.state?.from === '/invite') {
      setBannerErrorMessage('It looks like your email is not on the team list. Contact the team owner for access.');
      setRenderBanner(true);
    }
  }, [location?.state?.from]);

  /**
   * Function to resend confirmation code if got
   * UserNotConfirmed exception
   */
  const reconfirmSignUp = async (email: string): Promise<any> => {
    try {
      await Auth.resendSignUp(email);
      history.push('/confirm-code', { email });
    } catch (error) {
      setRenderBanner(false); // Reset banner state
      setHasError(false);
      switch (error.code) {
      case 'LimitExceededException':
      case 'TooManyRequestsException':
      case 'CodeDeliveryFailureException':
      case 'InternalErrorException':
      default:
        setBannerErrorMessage(error.message);
        break;
      }
      setBannerError();
    }
  };

  /**
   * Fired when user hits Login button.
   * Amplify will authenticate the user.
   * If successful, login() is called, if not, hasError
   * is set to true
   * @see https://docs.amplify.aws/lib/auth/mfa/q/platform/js#allow-users-to-select-mfa-type for more info on error codes
   */
  const onSubmit = async (data: Object): Promise<any> => {
    setIsLoading(true);
    const { email, password } = data || {};
    const trimmedEmail = email?.trim();

    const auth = {
      auth: { username: trimmedEmail, password, cognitoUser: undefined },
    };

    try {
      const user = await Auth.signIn({ username: trimmedEmail, password, validationData: { platform: 'web' } });

      // returned from user, if any
      const { challengeName } = user;
      auth.auth.cognitoUser = user;
      /**
       * User either sign in directly or
       * required to to pass some challenges before auth
       * challengeName is passed in the user object
       * Currently re-routes to login if there are challenges
       * To be modified further
       */
      switch (challengeName) {
      case 'SMS_MFA':
      case 'SOFTWARE_TOKEN_MFA':
        // User needs to input a code received from SMS to confirm sign in
        // or a OTP via Auth.confirmSignIn
        history.push('/confirm-code');
        break;

      case 'MFA_SETUP':
        // Happens when MFA method is the OTP,
        // requires user to to through steps
        // to generate those passwords. Start
        // set up via Auth.setupTOTP
        history.push('/');
        break;

      case 'NEW_PASSWORD_REQUIRED':
        // Happens when the user account is created through the Cognito console,
        // The user needs to input the new password and required attributes.
        history.push('/');
        break;

      default:
        login();
        break;
      }
    } catch (error) {
      setRenderBanner(false);
      setHasError(false);
      switch (error.code) {
      case 'UserNotConfirmedException':
        // User didn't finish the confirmation step when signing up
        // Resend the code and confirm the user
        reconfirmSignUp(trimmedEmail);
        break;

      case 'PasswordResetRequiredException':
        // Password was reset, needs to confirm email then reset password
        history.push('/confirm-email');
        break;

      case 'UserNotFoundException': // email/pw supplied doesn't exist in the Cognito pool
      case 'NotAuthorizedException': // incorrect password supplied
        setBannerErrorMessage(bannerMessageData.accountNotFoundError);
        setBannerError();
        break;

      case 'UserLambdaValidationException':
        setBannerErrorMessage(bannerMessageData.appUserError);
        setBannerError();
        break;

      default:
        setBannerErrorMessage(error.message);
        setBannerError();
        break;
      }
    }
    setIsLoading(false);
  };

  /**
   * Function to render Inputs for Controller render prop
   * of useForm hook
   */
  const renderInputs = ({ onChange, name }): React$Node => {
    let label = '';
    let variant = 'email'; // default type of inputs
    switch (name) {
    case 'password':
      label = 'Password';
      variant = 'password';
      break;

    case 'email':
    default:
      label = 'Email';
      break;
    }

    /**
     * Sets the value of input and if hasError is true, set to false
     * so user can start typing into the inputs again after errors
     */
    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
      (event?.target: HTMLInputElement);
      const value = event?.target?.value;
      if (name === 'email') {
        setUsername(value);
      }
      onChange(value);
    };

    /**
     * Function to clear any errors of inputs
     * when on focus event, if any
     */
    const onInputFocus = (): void => {
      if (errors?.[name]) clearErrors(name);
      if (hasError) setHasError(false);
    };

    return (
      <StyledInput
        variant={variant}
        error={!!errors?.[name] || hasError}
        errorLabel={errors?.[name]?.message || ''}
        inputName={name}
        label={label}
        onChange={onInputChange}
        onFocus={onInputFocus}
        required
        lowercase={variant === 'email'}
      />
    );
  };

  /**
   * Function to map out FormRows
   * that holds the Controller of each types of inputs
   * based on inputsData
   */
  const renderInputsControllers = (): React$Node => inputsData.data.map((field) => (
    <FormRow key={field.id}>
      <Controller
        control={control}
        render={renderInputs}
        name={field.name}
        defaultValue={field.defaultValue}
        rules={{
          required: 'Please enter a value to continue',
          pattern: {
            value: field?.regExPattern,
            message: field?.errorMessage,
          },
        }}
      />
    </FormRow>
  ));

  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */
  const isDisabled = (): boolean => {
    if (!inputsData?.data || !dirtyFields || isLoading) return true;
    return Object.keys(dirtyFields).length !== inputsData?.data?.length;
  };
  /**
   * Function to watch if an input has error
   * after user submit upon validation.
   * Errors object is {} by default - which means no error
   * Returns true if length of errors object != 0
   * and false if empty
   */
  const isError = (): boolean => !!Object.keys(errors).length;

  /**
   * To render ziynx logo as image
   */
  const renderLogo = (): React$Node => {
    if (isMobile) {
      return (
        <Image src={logoSmall} width="24px" height="24px" alt="company-logo" />
      );
    }
    return (
      <Image src={logo} width="114px" height="24px" alt="company-logo" />
    );
  };

  return (
    <>
      <Helmet>
        <title>Login | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={0} isVertical>
        {shouldRenderBanner && (
          <StyledBannerWrapper>
            <BannerNotification animated timeout={bannerTimeOut} variant="error">
              {bannerErrorMessage}
            </BannerNotification>
          </StyledBannerWrapper>
        )}
        <FormSectionWrapper
          isMobileSmall={isMobileSmall}
          isMobile={isMobile}
          hasBanner={shouldRenderBanner}
        >
          <ImageWrapper
            isMobile={isMobile}
          >
            {renderLogo()}
          </ImageWrapper>
          <Form
            formID="user-login"
            title="Log in to your account"
            autocomplete="off"
          >
            {renderInputsControllers()}
            <StyledBodyWrapper smallPadding half>
              <Link
                variant="navigation"
                activeOnlyWhenExact
                label="Forgot password?"
                to={{
                  pathname: '/confirm-email',
                  state: {
                    email: username,
                    sendCode: true,
                  },
                }}
              />
            </StyledBodyWrapper>
            <StyledButton
              disabled={isDisabled() || isError()}
              isLoading={isLoading}
              width="100%"
              type="submit"
              form="user-login"
              onClick={handleSubmit(onSubmit)}
            >
              Log in
            </StyledButton>
            {/* <StyledBodyWrapper center lastChild>
              <Body medium color={Colors.GrayVeryDark}>
                Don&apos;t have an account?&nbsp;
                <Link variant="navigation" activeOnlyWhenExact to="/sign-up" label="Sign up" />
              </Body>
            </StyledBodyWrapper> */}
          </Form>
        </FormSectionWrapper>
      </ContentContainer>
    </>
  );
};

LogInScreen.defaultProps = {
  viewport: 'desktop',
};

export default (withAppManager(LogInScreen): React$ComponentType<
  React$Config<ManagedProps<Props>, typeof LogInScreen.defaultProps>
>);
